import moment from 'moment';
import { nanosToMillis } from '@/datetime/dateTime.utilities';
import { normalizeTypes } from '@/homescreen/homescreen.actions';
import { HOME_SCREEN_SORT } from '@/homescreen/homescreen.constants';
import { SEARCH_ITEM_LOCATIONS } from '@/main/app.constants';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { cancelGroup } from '@/requests/pendingRequests.utilities';
import { errorToast } from '@/utilities/toast.utilities';
import { LocationEnum, SortFieldEnum, SortOrderEnum, sqFoldersApi } from '@seeqdev/api/endpoint/FoldersApi';
import type { HomeScreenItemType } from '@/homescreen/HomeScreenTable.organism';
import type {
  SearchOptions,
  SearchResults,
  SearchSuggestion,
  SearchSuggestions,
} from '@/homescreen/search/omniSearch.types';
import type { SearchItemLocation } from '@/main/app.constants';
import type { FolderSearchResultsV1 } from '@seeqdev/api/model/FolderSearchResultsV1';
import type { PermissionsV1 } from '@seeqdev/api/model/PermissionsV1';

const LOCATION_TO_FILTER: Partial<Record<SearchItemLocation, LocationEnum>> = {
  [SEARCH_ITEM_LOCATIONS.ALL]: LocationEnum.ALL,
  [SEARCH_ITEM_LOCATIONS.USERS]: LocationEnum.USERS,
  [SEARCH_ITEM_LOCATIONS.CORPORATE]: LocationEnum.CORPORATE,
  [SEARCH_ITEM_LOCATIONS.MY_FOLDER]: LocationEnum.MINE,
  [SEARCH_ITEM_LOCATIONS.SHARED_OR_PUBLIC]: LocationEnum.SHARED,
  [SEARCH_ITEM_LOCATIONS.EXTERNALLY_SHARED]: LocationEnum.EXTERNALLYSHARED,
} as const;

const SORT_FIELD_TO_SORT_ENUM = {
  [HOME_SCREEN_SORT.CREATED_AT]: SortFieldEnum.CreatedAt,
  [HOME_SCREEN_SORT.UPDATED_AT]: SortFieldEnum.UpdatedAt,
  [HOME_SCREEN_SORT.OWNER]: SortFieldEnum.Owner,
  [HOME_SCREEN_SORT.CREATOR]: SortFieldEnum.Creator,
  [HOME_SCREEN_SORT.NAME]: SortFieldEnum.Name,
} as const;

export async function getMeilisearchSuggestions(options: SearchOptions): Promise<SearchSuggestions> {
  const results = await search(options);
  return { suggestions: results ? mapToSuggestions(results) : [], facets: results?.facetDistribution ?? {} };
}

export async function getMeilisearchResults(options: SearchOptions): Promise<SearchResults> {
  const results = await search(options);

  return {
    results: mapToHomeScreenItems(results),
    facets: results.facetDistribution ?? {},
    path: results.path,
    totalResults: results.estimatedTotalHits ?? 0,
  };
}

async function search(options: SearchOptions): Promise<FolderSearchResultsV1> {
  const { params, config, cancellationGroup } = options;
  const filters = [];
  const types = normalizeTypes(params.types);
  if (types) {
    filters.push(`Type IN [${types.map((type) => `"${type}"`).join(', ')}]`);
  }
  if (params.ownerIds && params.ownerIds.length > 0) {
    filters.push(`Owner IN [${params.ownerIds.map((id) => `"${id}"`).join(', ')}]`);
  }
  if (params.creatorIds && params.creatorIds.length > 0) {
    filters.push(`Creator IN [${params.creatorIds.map((id) => `"${id}"`).join(', ')}]`);
  }
  const filter = filters.join(' AND ');

  const folderLocation = Object.values(SEARCH_ITEM_LOCATIONS).find((value) => value === params.folderId);
  const parentFolderId = !folderLocation ? params.folderId : undefined;
  const maybeLocation = (params.filter ?? folderLocation) as SearchItemLocation | undefined;
  let location = maybeLocation && LOCATION_TO_FILTER[maybeLocation] ? LOCATION_TO_FILTER[maybeLocation] : undefined;
  if (params.isArchived) {
    location = LocationEnum.TRASH;
  }

  let sortField: SortFieldEnum | undefined;
  let sortOrder: SortOrderEnum | undefined;
  if (params.sortOrder) {
    const [field, direction] = params.sortOrder.split(' ');
    sortField = SORT_FIELD_TO_SORT_ENUM[field] ? SORT_FIELD_TO_SORT_ENUM[field] : undefined;
    sortOrder = direction === 'asc' ? SortOrderEnum.ASC : SortOrderEnum.DESC;
  }

  if (cancellationGroup) {
    cancelGroup(cancellationGroup);
  }
  try {
    const { data } = await sqFoldersApi.search(
      {
        query: params.textSearch,
        filter,
        location,
        parentFolderId,
        sortField,
        sortOrder,
        hitsPerPage: config.resultsPerPage,
        page: config.page,
        hitHighlightFormat: config.highlightFormat,
      },
      { cancellationGroup },
    );

    return data;
  } catch (error) {
    errorToast({ httpResponseOrError: error });
    return {
      hits: [],
      facetDistribution: {},
      estimatedTotalHits: 0,
      hitsPerPage: 0,
      page: 1,
    };
  }
}

interface FolderSearchResultUserObject {
  readonly ID: string;
  readonly Name: string;
}

function mapToSuggestions(results: FolderSearchResultsV1): SearchSuggestion[] {
  return (results.hits ?? []).map((hit) => {
    const content = hit.Content as Record<string, string | number | boolean | FolderSearchResultUserObject>;
    const highlights = hit.Highlights as Record<string, string> | undefined;
    const owner = content.Owner as FolderSearchResultUserObject;
    const creator = content.Creator as FolderSearchResultUserObject;
    return {
      seeqId: content.ID as string,
      name: content.Name as string,
      owner: owner?.Name ?? '',
      creator: creator?.Name ?? '',
      highlightedName: highlights?.Name ?? (content.Name as string) ?? 'Unnamed Item',
      contentType: (content.Type as string) ?? '',
      createdAt: nanosToMillis(content['Created At'] as number),
      updatedAt: nanosToMillis(content['Updated At'] as number),
      workbookId: content.WorkbookId,
      worksheetId: content.WorksheetId,
      description: content.Description as string,
      highlightedDescription: highlights?.Description ?? undefined,
      sourceUrl: (content.SourceUrl as string) ?? undefined,
      parentItemName: (content.ParentItemName as string) ?? undefined,
      parentItemType: (content.ParentItemType as string) ?? undefined,
    };
  });
}

function mapToHomeScreenItems(results: FolderSearchResultsV1): HomeScreenItemType[] {
  return (results.hits ?? []).map((hit) => {
    const content = hit.Content as Record<
      string,
      string | number | boolean | string[] | FolderSearchResultUserObject | PermissionsV1
    >;
    const owner = content.Owner as FolderSearchResultUserObject;
    const creator = content.Creator as FolderSearchResultUserObject;
    return {
      id: content.ID as string,
      name: content.Name as string,
      createdAt: moment.utc(nanosToMillis(content['Created At'] as number)).toISOString(),
      updatedAt: moment.utc(nanosToMillis(content['Updated At'] as number)).toISOString(),
      owner: { id: owner?.ID ?? '', name: owner?.Name ?? '', isRedacted: owner?.Name === '[Redacted]' },
      creator: { id: creator?.ID ?? '', name: creator?.Name ?? '', isRedacted: creator?.Name === '[Redacted]' },
      isLocked: (content.Locked as boolean) ?? false,
      type: content.Type as string,
      breadcrumbPath: (content['Breadcrumb Path'] as string[]) ?? [],
      workbookId: content.WorkbookId,
      worksheetId: content.WorksheetId,
      ancestors: (content.Ancestors as string[]) ?? [],
      description: content.Description as string,
      sourceUrl: (content.SourceUrl as string) ?? undefined,
      effectivePermissions: content.effectivePermissions as PermissionsV1 | undefined,
      projectType: content[SeeqNames.Properties.ProjectType],
    };
  });
}
